'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Cell, Divider, Spacer, Tabs } from '@vinted/web-ui'
import classNames from 'classnames'
import { v4 as uuid } from 'uuid'

import { useBanners } from '@marketplace-web/banners/banners-feature'
import {
  FeedbackProvider,
  ProfileFeedback,
} from '@marketplace-web/profile/profile-feedback-feature'
import { SellerInsights } from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-feature'
import {
  SellerBadgeDto,
  SellerInsightsModel,
} from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-data'
import {
  TaxpayersSpecialVerificationSuccessModal,
  ProfileTaxpayerBanner,
} from '@marketplace-web/taxpayers/taxpayers-feature'
import {
  BumpCheckout,
  BumpCheckoutProvider,
  VasEntryPointsProvider,
} from '@marketplace-web/vas/vas-feature'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ShowAtBreakpoint, useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { FormattedMessage } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { removeParamsFromQuery, toUrlQuery } from '@marketplace-web/browser/url-util'
import { businessAccountFromUserDto } from '@marketplace-web/business-accounts/business-accounts-data'
import {
  BusinessAccountBanner,
  PoliciesModal,
  EprBanner,
} from '@marketplace-web/business-accounts/business-accounts-feature'
import {
  ProfileListingRestrictionBanner,
  ProfileSellingRestrictionBanner,
} from '@marketplace-web/user-restrictions/user-restrictions-feature'

import {
  PromoAfterItemUploadProvider,
  PromosAfterItemUpload,
} from '@marketplace-web/item-upload/after-upload-actions-feature'
import {
  clickEvent,
  viewScreenEvent,
  viewUserEvent,
  ProfileUserDto,
  WardrobeCatalogWithParentIdModel,
  ProfileUserInfoModel,
} from '@marketplace-web/profile/profile-data'
import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'
import { isBundlingAvailable } from '@marketplace-web/bundles/items-bundle-data'

import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'

import useWardrobeItems from '../../hooks/useWardrobeItems'
import InfoHeader from './InfoHeader'
import ProfileAdminSection from './ProfileAdminSection'
import ProfileBanners from './ProfileBanners'
import ProfileInfo from './ProfileInfo'
import UserInfoPanel from './UserInfoPanel/UserInfoPanel'
import UserItems from './UserItems'
import { isValueInObject } from '../../utils/object'

enum TabType {
  Closet = 'closet',
  Feedback = 'feedback',
  About = 'about',
  Insights = 'insights',
}

const TabClickableElements: Record<TabType, Parameters<typeof clickEvent>[0]['target']> = {
  closet: 'profile_tab',
  feedback: 'feedback_tab',
  about: 'about_tab',
  insights: 'insights_tab',
}

type ActiveTabProps = {
  showActions: boolean
}

type Props = {
  user: ProfileUserDto
  homepageSessionId: string | null
  currentUser: ProfileUserDto | null
  userInfo: ProfileUserInfoModel
  catalogs: Array<WardrobeCatalogWithParentIdModel>
  sellerBadgesList?: Array<Omit<SellerBadgeDto, 'progress'>>
  sellerInsights?: SellerInsightsModel
}

const isTabType = (tabType: unknown): tabType is TabType => isValueInObject(tabType, TabType)

const Profile = ({
  user,
  homepageSessionId,
  currentUser,
  userInfo,
  catalogs,
  sellerBadgesList,
  sellerInsights,
}: Props) => {
  const breakpoints = useBreakpoint()
  const { replaceHistoryState, relativeUrl, searchParams } = useBrowserNavigation()
  const { track } = useTracking()

  const isKillswitchBannersEnabled = useFeatureSwitch('killswitch_banners_web')

  const {
    items,
    itemsCount,
    totalItemsCount,
    isLoading,
    sortBy,
    catalogId,
    selectedSellerFilter,
    fetchMoreItems,
    updateFilters,
  } = useWardrobeItems({ userId: user.id })
  const { fetchBanners, banners } = useBanners()

  const defaultActiveTab = isTabType(searchParams.tab) ? searchParams.tab : TabType.Closet

  const isContentTranslationEnabled = useFeatureSwitch('content_translation_web')

  const holidayModeRedirectAbTest = useAbTest('holiday_mode_redirect')

  const profileReworkAbTest = useAbTest('profile_wardrobe_web_rework')
  const isProfileReworkTestOn = profileReworkAbTest?.variant === 'on' && !breakpoints.portables
  const bundlingSellerProfileAbTest = useAbTest('bundling_seller_profile_v2')
  const isBundlingSellerProfileAbTestOn =
    bundlingSellerProfileAbTest?.variant === 'a' || bundlingSellerProfileAbTest?.variant === 'b'

  const [activeTabId, setActiveTabId] = useState(defaultActiveTab)
  const [isPoliciesModalOpen, setIsPoliciesModalOpen] = useState(!!searchParams.policy)
  const [isInitialFetchDone, setIsInitialFetchDone] = useState(false)
  const [itemIdsInUrl, setItemIdsInUrl] = useState<Array<number> | null>(null)
  const [selectedItems, setSelectedItems] = useState<Array<ProductItemModel>>([])
  const bundleTrackingId = useMemo(() => uuid(), [])

  const isViewingSelf = currentUser?.id === user.id
  const screenName = isViewingSelf ? 'current_user_profile' : 'user_profile'

  const isProfileReworkRendered = isProfileReworkTestOn && isViewingSelf
  const showBundles = isBundlingAvailable({
    canBundle: !!user?.can_bundle,
    totalItemCount: totalItemsCount,
    isCurrentUserAuthenticated: !!currentUser,
    closetCountdownEndDate: user?.closet_countdown_end_date,
    hasHateFromAnySide: !!user?.is_hated || !!user?.hates_you,
    isViewingOwnProfile: isViewingSelf,
  })

  const hasValidItemIds = (itemIds: Array<number> | null) =>
    Array.isArray(itemIds) && itemIds.every(id => !Number.isNaN(Number(id)))

  const handleChangeTab = useCallback(
    (tabId: TabType) => {
      setActiveTabId(tabId)

      const validIds = hasValidItemIds(itemIdsInUrl) ? itemIdsInUrl! : []

      replaceHistoryState(
        `${relativeUrl}?${toUrlQuery({
          tab: tabId,
          ...(validIds.length > 0 && { item_ids: validIds }),
          ...(homepageSessionId && { homepage_session_id: homepageSessionId }),
        })}`,
      )
    },
    [homepageSessionId, itemIdsInUrl, relativeUrl, replaceHistoryState],
  )

  useTrackAbTest(
    holidayModeRedirectAbTest,
    // TODO: why we only track exposure for `on`?
    !!(holidayModeRedirectAbTest?.variant === 'on' && searchParams.on_holiday),
  )
  useTrackAbTest(profileReworkAbTest, isViewingSelf)

  useEffect(() => {
    if (isInitialFetchDone) return

    setIsInitialFetchDone(true)
    fetchMoreItems()
  }, [isInitialFetchDone, fetchMoreItems])

  useEffect(() => {
    if (!isViewingSelf) return
    if (isKillswitchBannersEnabled) return

    fetchBanners()
  }, [isKillswitchBannersEnabled, isViewingSelf, fetchBanners])

  useEffect(() => {
    const refererPath = document.referrer ? new URL(document.referrer).pathname : 'unknown'

    track(viewUserEvent({ path: refererPath, user_id: user.id }))
  }, [track, user.id])

  useEffect(() => {
    track(
      viewScreenEvent({
        screen: screenName,
      }),
    )
  }, [screenName, track])

  useEffect(() => {
    if ([TabType.Closet, TabType.Feedback, TabType.Insights].includes(activeTabId)) return
    if (breakpoints.phones && activeTabId === TabType.About) return

    handleChangeTab(TabType.Closet)
  }, [activeTabId, breakpoints.phones, handleChangeTab])

  useEffect(() => {
    if (!isBundlingSellerProfileAbTestOn || !isInitialFetchDone) {
      return
    }

    // Only works on second component render
    replaceHistoryState(
      removeParamsFromQuery(window.location.pathname, window.location.search, ['item_ids']),
    )
  }, [isBundlingSellerProfileAbTestOn, replaceHistoryState, isInitialFetchDone])

  const onTabChange: React.ComponentProps<typeof Tabs<TabType>>['onClick'] = tab => {
    handleChangeTab(tab.id)

    const screen = isViewingSelf ? 'current_user_profile' : 'user_profile'
    track(clickEvent({ screen, target: TabClickableElements[tab.id] }))
  }

  const changeTab = (tabId: TabType) => () => {
    handleChangeTab(tabId)
  }

  const togglePoliciesModal = () => {
    setIsPoliciesModalOpen(prevState => !prevState)
  }

  const getTabs = () => {
    const tabs = [
      {
        id: TabType.Closet,
        title: <FormattedMessage id="profile.tabs.wardrobe_rename" />,
      },
      { id: TabType.Feedback, title: <FormattedMessage id="profile.tabs.feedback" /> },
    ] as React.ComponentProps<typeof Tabs<TabType>>['items']

    const isInsightsTabVisible = sellerInsights?.isVisible && isViewingSelf

    if (isInsightsTabVisible) {
      tabs.push({
        id: TabType.Insights,
        title: <FormattedMessage id="profile.seller_insights.wardrobe_tab" />,
      })
    }

    if (breakpoints.phones) {
      tabs.push({ id: TabType.About, title: <FormattedMessage id="profile.tabs.about" /> })
    }

    return tabs
  }

  const renderWarningBlock = (keyId: string) => (
    <>
      <Cell body={<FormattedMessage id={keyId} />} theme="warning" />
      <Spacer size={Spacer.Size.Large} />
    </>
  )

  const renderItemsWarning = () => {
    const isViewedByGod = currentUser?.is_god
    const isViewedByModerator = currentUser?.moderator

    if (!(isViewingSelf || isViewedByGod || isViewedByModerator)) return null

    if (user.has_replica_proof_items) {
      return renderWarningBlock('profile.notification.replica_warning')
    }

    if (!user.has_item_alerts) return null

    return renderWarningBlock('profile.notification.items_warning')
  }

  const mainAreaClasses = classNames({
    'u-flexbox u-flex-direction-column u-flex-1': isProfileReworkTestOn,
  })

  const handleBundleItemSelect = (itemIds: Array<number> | null) => {
    setItemIdsInUrl(itemIds)
    replaceHistoryState(
      `${relativeUrl}?${toUrlQuery({
        tab: activeTabId,
        ...(!!itemIds?.length && { item_ids: itemIds }),
        ...(homepageSessionId && { homepage_session_id: homepageSessionId }),
      })}`,
    )
  }

  const handleItemLimitationError = (lastSelectedItemId: string) => {
    const filteredIds = itemIdsInUrl?.filter(id => id !== Number(lastSelectedItemId))

    if (!filteredIds) return

    const validIds = hasValidItemIds(filteredIds) ? filteredIds! : []
    setItemIdsInUrl(validIds)

    setSelectedItems(selectedItems.filter(item => item.id !== Number(lastSelectedItemId)))
    replaceHistoryState(
      `${relativeUrl}?${toUrlQuery({
        tab: activeTabId,
        ...(validIds.length > 0 && { item_ids: validIds }),
        ...(homepageSessionId && { homepage_session_id: homepageSessionId }),
      })}`,
    )
  }

  const renderClosetTab = (activeTabProps: ActiveTabProps) => {
    const { showActions } = activeTabProps

    if (breakpoints.tabletsUp) {
      return (
        <BumpCheckoutProvider>
          <div className={classNames('profile__items-wrapper')}>
            <div>
              <InfoBanner screen={screenName} theme="inverseExperimental" />
              <Spacer size={Spacer.Size.Medium} />
              <ProfileBanners
                user={user}
                showActions={showActions}
                viewingSelf={isViewingSelf}
                showBundles={showBundles}
                userId={user.id}
              />
              <BumpCheckout />
            </div>
            <UserItems
              userId={user.id}
              allowBump
              userOnHoliday={!!user?.is_on_holiday}
              currentUserId={currentUser?.id}
              items={items}
              itemsCount={itemsCount}
              isLoading={isLoading}
              selectedSellerFilter={selectedSellerFilter}
              sortBy={sortBy}
              screen="user_profile"
              catalogId={catalogId}
              catalogs={catalogs}
              fetchMoreItems={fetchMoreItems}
              updateFilters={updateFilters}
              totalItemsCount={totalItemsCount}
              homepageSessionId={homepageSessionId}
              onBundleItemSelect={handleBundleItemSelect}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              showBundles={showBundles}
              bundleTrackingId={bundleTrackingId}
              isBundleDiscountEnabled={user.bundle_discount?.enabled ?? false}
              onItemLimitationError={handleItemLimitationError}
            />
          </div>
        </BumpCheckoutProvider>
      )
    }

    return (
      <BumpCheckoutProvider>
        <div className="u-ui-margin-large">
          <InfoBanner screen={screenName} theme="inverseExperimental" />
        </div>
        <div>
          {renderItemsWarning()}
          <SeparatedList separator={<Divider />}>
            {banners.businessAccount && isViewingSelf && (
              <BusinessAccountBanner banner={banners.businessAccount} />
            )}
            {banners.businessAccountEpr && isViewingSelf && (
              <EprBanner banner={banners.businessAccountEpr} />
            )}
            {user && (
              <ProfileInfo
                user={user}
                sellerBadgesList={sellerBadgesList}
                {...userInfo}
                showActions={showActions}
                viewingSelf={isViewingSelf}
                onTogglePoliciesModal={togglePoliciesModal}
                screen="user_profile"
                compactMode
                onUserInfoClick={changeTab(TabType.Feedback)}
                onAboutClick={changeTab(TabType.About)}
                isContentTranslationEnabled={isContentTranslationEnabled}
              />
            )}
            <>
              <Spacer size={Spacer.Size.Medium} />
              <ProfileBanners
                user={user}
                showActions={showActions}
                viewingSelf={isViewingSelf}
                showBundles={showBundles}
                userId={user.id}
              />
            </>
          </SeparatedList>
          <BumpCheckout />
        </div>
        <UserItems
          userId={user.id}
          allowBump
          userOnHoliday={!!user?.is_on_holiday}
          currentUserId={currentUser?.id}
          items={items}
          itemsCount={itemsCount}
          isLoading={isLoading}
          selectedSellerFilter={selectedSellerFilter}
          sortBy={sortBy}
          screen="user_profile"
          catalogId={catalogId}
          catalogs={catalogs}
          fetchMoreItems={fetchMoreItems}
          updateFilters={updateFilters}
          totalItemsCount={totalItemsCount}
          homepageSessionId={homepageSessionId}
          onBundleItemSelect={handleBundleItemSelect}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          showBundles={showBundles}
          bundleTrackingId={bundleTrackingId}
          isBundleDiscountEnabled={user.bundle_discount?.enabled ?? false}
          onItemLimitationError={handleItemLimitationError}
        />
      </BumpCheckoutProvider>
    )
  }

  const renderAboutTab = (activeTabProps: ActiveTabProps) => {
    const { showActions } = activeTabProps

    return (
      <ProfileInfo
        user={user}
        sellerBadgesList={sellerBadgesList}
        {...userInfo}
        showActions={showActions}
        viewingSelf={isViewingSelf}
        onTogglePoliciesModal={togglePoliciesModal}
        screen="user_info"
        onUserInfoClick={changeTab(TabType.Feedback)}
        isContentTranslationEnabled={isContentTranslationEnabled}
      />
    )
  }

  const renderFeedbackTab = () => {
    return (
      <div className={classNames('profile__items-wrapper')}>
        <FeedbackProvider>
          <ProfileFeedback
            isContentTranslationEnabled={isContentTranslationEnabled}
            viewingSelf={isViewingSelf}
            userId={user.id}
            username={user.login}
            showTitle={false}
          />
        </FeedbackProvider>
      </div>
    )
  }

  const renderInsightsTab = () => {
    if (!sellerInsights) return null

    return <SellerInsights sellerInsights={sellerInsights} isSellerInsightsLoading={false} />
  }

  const renderActiveTab = (activeTabProps: ActiveTabProps) => {
    switch (activeTabId) {
      case TabType.Closet:
        return renderClosetTab(activeTabProps)
      case TabType.Feedback:
        return renderFeedbackTab()
      case TabType.About:
        return renderAboutTab(activeTabProps)
      case TabType.Insights:
        return renderInsightsTab()
      default:
        return null
    }
  }

  const renderPoliciesModal = () => {
    if (!user?.business) return null

    return (
      <PoliciesModal
        show={isPoliciesModalOpen}
        onClose={togglePoliciesModal}
        business={user.business}
        businessAccount={businessAccountFromUserDto(user)}
      />
    )
  }

  const { showSupportFunctionality } = userInfo

  const showActions = !(user?.is_hated || user?.hates_you)

  return (
    <PromoAfterItemUploadProvider>
      <VasEntryPointsProvider userId={user.id}>
        {user && showSupportFunctionality && <ProfileAdminSection userId={user.id} />}
        {isViewingSelf ? (
          <>
            <ProfileListingRestrictionBanner accountRestriction={currentUser?.action_restriction} />
            <ProfileSellingRestrictionBanner accountRestriction={currentUser?.action_restriction} />
          </>
        ) : null}
        <ShowAtBreakpoint breakpoint="phones">
          <Tabs
            items={getTabs()}
            width={Tabs.Width.Parent}
            activeItemId={activeTabId}
            onClick={onTabChange}
          />
          <Divider />
          {renderActiveTab({ showActions })}
        </ShowAtBreakpoint>
        <ShowAtBreakpoint breakpoint="tabletsUp">
          <div className="container">
            {isProfileReworkTestOn && <ProfileTaxpayerBanner />}
            {banners.businessAccountEpr && isViewingSelf && isProfileReworkTestOn && (
              <>
                <EprBanner banner={banners.businessAccountEpr} />
                <Spacer />
              </>
            )}
            <div
              className={`profile u-flex-direction-${isProfileReworkRendered ? 'row' : 'column'}`}
            >
              <div>
                {renderItemsWarning()}
                {banners.businessAccount && isViewingSelf && (
                  <>
                    <BusinessAccountBanner banner={banners.businessAccount} />
                    <Spacer size={Spacer.Size.X3Large} />
                  </>
                )}
                {banners.businessAccountEpr && isViewingSelf && !isProfileReworkTestOn && (
                  <>
                    <EprBanner banner={banners.businessAccountEpr} />
                    <Spacer size={Spacer.Size.X3Large} />
                  </>
                )}
                {user && !isProfileReworkRendered && (
                  <InfoHeader
                    user={user}
                    sellerBadgesList={sellerBadgesList}
                    {...userInfo}
                    showActions={showActions}
                    viewingSelf={isViewingSelf}
                    screen={screenName}
                    onTogglePoliciesModal={togglePoliciesModal}
                    onRatingClick={changeTab(TabType.Feedback)}
                    isContentTranslationEnabled={isContentTranslationEnabled}
                  />
                )}
                {user && isProfileReworkRendered && (
                  <UserInfoPanel
                    user={user}
                    {...userInfo}
                    screen={screenName}
                    onTogglePoliciesModal={togglePoliciesModal}
                    onRatingClick={changeTab(TabType.Feedback)}
                    isContentTranslationEnabled={isContentTranslationEnabled}
                  />
                )}
              </div>
              {isProfileReworkTestOn ? (
                <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.X3Large} />
              ) : (
                <Spacer orientation={Spacer.Orientation.Horizontal} size={Spacer.Size.Large} />
              )}
              <div className={mainAreaClasses}>
                <span id="tabs">
                  <Tabs items={getTabs()} activeItemId={activeTabId} onClick={onTabChange} />
                </span>
                <Divider />
                {!isProfileReworkTestOn && <Spacer />}
                {renderActiveTab({ showActions })}
              </div>
            </div>
          </div>
        </ShowAtBreakpoint>
        <PromosAfterItemUpload />
        {renderPoliciesModal()}
        <TaxpayersSpecialVerificationSuccessModal />
      </VasEntryPointsProvider>
    </PromoAfterItemUploadProvider>
  )
}

export default Profile
